import { useMutation } from "@apollo/client";
import { useState } from "react";
import { createKeywordGroup } from "../graphql/mutations/createKeywordGroup";
import { deleteKeywordGroup } from "../graphql/mutations/deleteKeywordGroup";
import { updateKeywordGroup } from "../graphql/mutations/updateKeywordGroup";
import { useToast } from "./useToast";

export function useKeywordModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState("add"); // "add", "edit", or "delete"
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [executeCreateKeyword] = useMutation(createKeywordGroup);
  const [executeUpdateKeyword] = useMutation(updateKeywordGroup);
  const [executeDeleteKeyword] = useMutation(deleteKeywordGroup);
  const { toast, showToast, closeToast } = useToast();

  const openModal = (modalMode = "add", keywordData = {}) => {
    setMode(modalMode);
    setFormData(keywordData);
    setFormErrors([]);
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    setFormData({});
    setFormErrors([]);
    setMode("add");
  };

  const handleFormChange = (data, errors) => {
    setFormData(data);
    setFormErrors(errors || []);
  };

  const validateForm = () => {
    const errors = [];

    if (!formData.name || formData.name.trim() === "") {
      errors.push({ instancePath: "/name", message: "Name is required" });
    }

    if (!formData.slug || formData.slug.trim() === "") {
      errors.push({ instancePath: "/slug", message: "Slug is required" });
    }

    if (!formData.type || formData.type.trim() === "") {
      errors.push({ instancePath: "/type", message: "Type is required" });
    }

    if (
      !formData.keywords ||
      !Array.isArray(formData.keywords) ||
      formData.keywords.length === 0
    ) {
      errors.push({
        instancePath: "/keywords",
        message: "At least one keyword is required",
      });
    }

    return errors;
  };

  const handleSubmit = async (onSuccess) => {
    if (mode === "delete") {
      return handleDelete(onSuccess);
    }

    const validationErrors = validateForm();

    if (validationErrors.length > 0) {
      setFormErrors(validationErrors);
      showToast(
        "error",
        "Validation Error",
        "Please fix the validation errors before submitting"
      );
      return { isSuccess: false, message: "Please fix the validation errors" };
    }

    setIsLoading(true);

    try {
      const keywordPayload = {
        name: formData.name,
        slug: formData.slug,
        imageUrl: formData.imageUrl,
        type: formData.type,
        keywords: formData.keywords || [],
      };

      let response;
      let message;

      if (mode === "edit" && formData.id) {
        response = await executeUpdateKeyword({
          variables: {
            id: formData.id,
            ...keywordPayload,
          },
        });
        message = "Keyword updated successfully";
      } else {
        response = await executeCreateKeyword({
          variables: keywordPayload,
        });
        message = "Keyword created successfully";
      }

      setIsLoading(false);
      showToast(
        "success",
        "Keyword Saved",
        "The keyword has been saved successfully"
      );
      closeModal();

      if (onSuccess) {
        onSuccess();
      }

      return {
        isSuccess: true,
        message,
        data: response.data,
      };
    } catch (error) {
      setIsLoading(false);
      return {
        isSuccess: false,
        message: error.message || "An error occurred while saving the keyword",
      };
    }
  };

  const handleDelete = async (onSuccess) => {
    if (!formData.id) {
      return {
        isSuccess: false,
        message: "No keyword ID provided for deletion",
      };
    }

    setIsLoading(true);

    try {
      await executeDeleteKeyword({
        variables: {
          id: formData.id,
        },
      });

      setIsLoading(false);
      showToast(
        "success",
        "Keyword Deleted",
        "The keyword has been deleted successfully"
      );
      closeModal();

      if (onSuccess) {
        onSuccess();
      }

      return {
        isSuccess: true,
        message: "Keyword deleted successfully",
      };
    } catch (error) {
      setIsLoading(false);
      showToast(
        "error",
        "Delete Failed",
        error.message || "An error occurred while deleting the keyword"
      );
      return {
        isSuccess: false,
        message: error.message || "An error occurred while deleting the keyword",
      };
    }
  };

  return {
    isOpen,
    mode,
    formData,
    formErrors,
    isLoading,
    openModal,
    closeModal,
    handleFormChange,
    handleSubmit,
    toast,
    showToast,
    closeToast,
  };
}
